import type IFiltersNew from "../types/IFiltersNew";
import {formatDateStringToDate} from "./DateFormatting";

export type FilterPredicate<T> = (item: T, value: NonNullable<IFiltersNew[keyof IFiltersNew]>) => boolean;
export type FilterStrategies<T> = Partial<Record<keyof IFiltersNew, FilterPredicate<T>>>;

interface DefaultFilterItem {
  createdOn: string;
  tags: string[];
  locationCode: string;
  createdBy: string;
  name: string;
};

const defaultFilterStrategies: FilterStrategies<DefaultFilterItem> = {
  from: (item, value) =>
    formatDateStringToDate(item.createdOn) >=
    formatDateStringToDate(value as string),
  to: (item, value) =>
    formatDateStringToDate(item.createdOn) <=
    formatDateStringToDate(value as string),
  tags: (item, value) =>
    item.tags.some((tag) => (value as string[]).includes(tag)),
  locationCodes: (item, value) =>
    (value as string[]).includes(item.locationCode),
  createdBy: (item, value) =>
    (value as string[]).includes(item.createdBy),
  name: (item, value) =>
    item.name.toLowerCase().includes((value as string).toLowerCase()),
};

export const getPaginatedData = <T>(
  data: T[],
  pageSize: number,
  currentIndex: number,
): T[] => {
  const {fromIndex, toIndex} = calculationFromToIndex(
    data.length,
    pageSize,
    currentIndex,
  );

  return data.slice(fromIndex, toIndex);
};

export const filterData = <T extends DefaultFilterItem>(
  data: T[] | undefined,
  filters: IFiltersNew,
  strategies: FilterStrategies<T> = defaultFilterStrategies,
): T[] | undefined => {
  return data?.filter((item: T) =>
    (Object.entries(filters) as Array<[keyof IFiltersNew, IFiltersNew[keyof IFiltersNew]]>).every(
      ([key, value]) => {
        if (value === undefined || (Array.isArray(value) ? value.length === 0 : value === "")) {
          return true;
        }
        const predicate = strategies[key];
        return predicate ? predicate(item, value) : true;
      },
    ),
  );
};

export const filterDataWithPagination = <T extends DefaultFilterItem>(
  data: T[] | undefined,
  filters: IFiltersNew,
  pageSize: number,
  currentIndex: number,
): T[] | undefined => {
  const filtered = filterData(data, filters);

  if (filtered !== undefined) {
    return getPaginatedData(filtered, pageSize, currentIndex);
  }

  return filtered;
};

const calculationFromToIndex = (
  total: number,
  pageSize: number,
  currentIndex: number,
): {fromIndex: number; toIndex: number} => {
  const fromIndex = currentIndex * pageSize;
  const toIndex = Math.min((currentIndex + 1) * pageSize, total);

  return {fromIndex, toIndex};
};
